/**
 * Canvas 2D renderer for the Flash-compatibility runtime's display tree.
 *
 * Reads the tree only through the renderer seam (`renderData`, src/flash/display/render-data.ts)
 * and draws it every frame:
 * - concatenated matrices (stage -> device view matrix x local matrices; scrollRect offsets);
 * - colour transforms: leaf-level like Flash (they concatenate down to shapes/bitmaps/text);
 *   alpha-only transforms use globalAlpha, general ones use cached tinted copies of the source
 *   pixels (computed from the straight RGBA of the decoded image / BitmapData, no canvas loss);
 * - timeline shapes / morph shapes from their rasterized ImageRefs (atlas sub-rectangles, image
 *   scale, offsets; morphs use the sample for the current ratio);
 * - Bitmap/BitmapData with incremental texture uploads ($version / $takeDirty), smoothing flag and
 *   Flash pixel snapping;
 * - TextField (runtime layout, fonts via FontResolver, colours, border/background, selection,
 *   blinking caret), Graphics vector commands;
 * - masks: `mask` property and timeline clip layers (clipDepth): offscreen layer + destination-in,
 *   or a clip path when the mask is a fully opaque rectangular image (fast path);
 * - scrollRect clipping, visibility, blend modes (normal, layer, add, multiply, screen, lighten,
 *   darken, difference, overlay, hardlight; subtract/invert/erase/alpha approximated);
 * - filters (Blur, Glow, DropShadow, Bevel, ColorMatrix) in software on an offscreen layer; colour
 *   matrices that commute with compositing (the game's hue filters: red water, napalm) are instead
 *   distributed to the leaves as extra colour stages (cached tinted copies, no layer);
 * - culling of leaves outside the visible area.
 * Content inside filtered/masked/blended objects is rendered into a layer without the object's
 * own colour transform, which is then applied to the result (Flash cacheAsBitmap semantics).
 * cacheAsBitmap itself is not cached (no visual difference).
 *
 * Works with DOM canvases, OffscreenCanvas and @napi-rs/canvas (tests): pass `canvasFactory`
 * when neither OffscreenCanvas nor `document` exist.
 */
import * as rd from '../display/render-data';
import type { DisplayObject } from '../display/DisplayObject';
import { FontResolver } from '../platform/browser-fonts';
import {
  Box,
  CM,
  CT,
  ColorStages,
  IDENTITY_CT,
  Mat,
  applyColorToRGBA,
  boxEmpty,
  boxIntersect,
  boxIntersects,
  boxPad,
  boxRoundOut,
  concatCT,
  cssColor,
  invert,
  isAlphaOnlyCT,
  isDistributableMatrix,
  isIdentityCT,
  isIdentityMatrix,
  isInvisibleColor,
  mul,
  rectBox,
  translate,
} from './math';
import { CanvasFactory, Surface, SurfaceOptions, Surfaces, defaultCanvasFactory } from './surfaces';
import { FilterSpec, applyFilterSpec, filterPadding, filterSpec } from './filters';
import { drawGraphics } from './graphics';
import { drawText } from './text';
import { DamageTracker, type DamagePlan } from './damage';

/** Stage -> device mapping: device = stage * scale + (tx, ty). */
export interface Viewport {
  scale: number;
  /** Vertical scale (default: `scale`). */
  scaleY?: number;
  tx: number;
  ty: number;
}

export interface CanvasRendererOptions {
  /** Creates offscreen canvases (layers, textures). Default: OffscreenCanvas or <canvas>. */
  canvasFactory?: CanvasFactory;
  /** Font resolver shared with the platform's measureText (default: system fonts only). */
  fonts?: FontResolver;
  /**
   * Called for shape images that are not decoded yet (AssetLibrary shapes: 'lazy'), e.g.
   * `(url) => library.images.load(url)`. When the promise resolves `onImageLoaded` fires.
   */
  imageLoader?: ((url: string) => Promise<unknown> | void) | null;
  onImageLoaded?: (() => void) | null;
  /** Draw text fields (default true). */
  drawText?: boolean;
  /** Caret blink half-period in ms (default 500; 0 = always visible). */
  caretBlinkMs?: number;
  /** Clock for the caret blink (default performance.now). */
  now?: () => number;
  /** Smoothing of rasterized vector shapes (default true). */
  shapeSmoothing?: boolean;
  /** Smoothing of shape ImageRefs that are raw bitmap fills ("bitmaps/..."; default false, like the reference compositor). */
  bitmapFillSmoothing?: boolean;
  /**
   * Sharp bilinear for nearest-sampled raw texels (non-smoothed bitmap fills, Bitmaps with smoothing=false) drawn
   * magnified: draw a cached integer nearest prescale with bilinear smoothing instead of plain nearest (uneven
   * blocky texels at 1.33x/1.8x). Only applies above `sharpBilinearMin` device px per texel. Default true.
   */
  sharpBilinear?: boolean;
  sharpBilinearMin?: number;
  /** Ignore offline HD render variants (ImageRef.hd); for comparisons. Default false. */
  disableHd?: boolean;
  /**
   * Cache axis-aligned BitmapData drawn with sharp bilinear (the terrain and other bitmaps of 64K+ device px) at
   * device resolution and blit them 1:1 (see Surfaces.drawDeviceBitmap); otherwise the integer prescale is resampled
   * every frame. Default true.
   */
  deviceBitmapCache?: boolean;
  /**
   * imageSmoothingQuality for smoothed images (default 'low' = bilinear). Shape rasters are 2x
   * images drawn at ~0.5 scale, where bilinear sampling already averages 2x2 texels; 'medium' and
   * 'high' (mipmaps / bicubic) cost ~4x more per draw on software-rendered canvases (measured in
   * headless Chromium: 50 draws of 200x200 at 0.5 scale: low 1.5 ms, high 6 ms).
   */
  imageSmoothingQuality?: 'low' | 'medium' | 'high';
  surfaces?: SurfaceOptions;
  /** Pixel budget of cached filter results (default 16M px; 0 disables the cache). */
  filterCacheBudget?: number;
  /** Largest filtered object (device px area, incl. filter padding) that is cached (default 1M px). */
  filterCacheMaxArea?: number;
  /**
   * PORT (mobile performance): damage rendering of renderStage (src/flash/render/damage.ts): a frame redraws only the
   * device rectangles its changes cover, the rest of the canvas keeps the last frame's (identical) pixels. The canvas
   * must keep its pixels between frames and be drawn by this renderer only. Default false.
   */
  damage?: boolean;
}

export interface RenderStats {
  nodes: number;
  images: number;
  bitmaps: number;
  texts: number;
  layers: number;
  /** Clip rectangles used for opaque rectangular masks (fast path). */
  clipMasks: number;
  /** Masked groups drawn (timeline clip layers + `mask` property). */
  maskGroups: number;
  culled: number;
  filterCacheHits: number;
  /** Raw bitmap fills drawn from their offline HD (xBR) variant. */
  hdImages: number;
}

interface FilterCacheEntry {
  sig: number[];
  key: string;
  m: Mat;
  /** Device box relative to (floor(m.tx), floor(m.ty)). */
  rel: Box;
  surface: Surface;
  pixels: number;
  lastFrame: number;
}

function sameSig(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

interface Target {
  ctx: CanvasRenderingContext2D;
  /** Visible region of this target in its device pixels (culling / layer sizing). */
  clip: Box;
  /** Rendering into an offscreen layer (erase/alpha blend modes apply here). */
  isLayer: boolean;
}

const COMPOSITE: Record<string, GlobalCompositeOperation> = {
  normal: 'source-over',
  layer: 'source-over',
  multiply: 'multiply',
  screen: 'screen',
  lighten: 'lighten',
  darken: 'darken',
  difference: 'difference',
  add: 'lighter',
  overlay: 'overlay',
  hardlight: 'hard-light',
  // UNVERIFIED approximations (no Canvas equivalent):
  subtract: 'difference',
  erase: 'destination-out',
  alpha: 'destination-in',
};

const IDENTITY: Mat = [1, 0, 0, 1, 0, 0];

/** BitmapData above this many pixels count as "large" for the quality ladder (terrain maps). */
const LARGE_BITMAP_PX = 512 * 1024;

function emptyStats(): RenderStats {
  return { nodes: 0, images: 0, bitmaps: 0, texts: 0, layers: 0, clipMasks: 0, maskGroups: 0, culled: 0, filterCacheHits: 0, hdImages: 0 };
}

export class CanvasRenderer {
  readonly ctx: CanvasRenderingContext2D;
  readonly surfaces: Surfaces;
  readonly fonts: FontResolver;
  /** Statistics of the last rendered frame. */
  stats: RenderStats = emptyStats();
  private readonly opts: Required<Omit<CanvasRendererOptions, 'canvasFactory' | 'fonts' | 'surfaces' | 'imageLoader' | 'onImageLoaded' | 'filterCacheBudget' | 'filterCacheMaxArea' | 'damage'>>;
  private readonly imageLoader: ((url: string) => Promise<unknown> | void) | null;
  private readonly onImageLoaded: (() => void) | null;
  private readonly requested = new Set<string>();
  /** Stage (global) space -> device, for masks set through the `mask` property. */
  private rootToDevice: Mat = [1, 0, 0, 1, 0, 0];
  /** Filter scale (device px per stage px). */
  private fsx = 1;
  private fsy = 1;
  private caretOn = true;
  /**
   * Colour stages applied after the leaf colour transform (outermost first) while drawing the
   * subtree of an object whose ColorMatrixFilters are distributed to its leaves; null otherwise.
   */
  private outer: ColorStages | null = null;
  private readonly filterCacheBudget: number;
  private readonly filterCacheMaxArea: number;
  /** Damage rendering (options.damage), else null. */
  readonly damage: DamageTracker | null;
  /** The rectangles a partial frame redraws (leaf culling on the canvas; null: whole frame or one rectangle). */
  private damageRects: Box[] | null = null;
  /** A partial frame's rectangles (subtree culling by the damage records on the canvas; null: a whole frame). */
  private damageAll: Box[] | null = null;

  constructor(ctx: CanvasRenderingContext2D, opts: CanvasRendererOptions = {}) {
    this.ctx = ctx;
    this.surfaces = new Surfaces(opts.canvasFactory ?? defaultCanvasFactory, opts.surfaces);
    this.fonts = opts.fonts ?? new FontResolver();
    this.imageLoader = opts.imageLoader ?? null;
    this.onImageLoaded = opts.onImageLoaded ?? null;
    this.filterCacheBudget = opts.filterCacheBudget ?? 16 * 1024 * 1024;
    this.filterCacheMaxArea = opts.filterCacheMaxArea ?? 1024 * 1024;
    this.damage = opts.damage ? new DamageTracker() : null;
    this.opts = {
      drawText: opts.drawText ?? true,
      caretBlinkMs: opts.caretBlinkMs ?? 500,
      now: opts.now ?? (() => performance.now()),
      shapeSmoothing: opts.shapeSmoothing ?? true,
      bitmapFillSmoothing: opts.bitmapFillSmoothing ?? false,
      sharpBilinear: opts.sharpBilinear ?? true,
      sharpBilinearMin: opts.sharpBilinearMin ?? 1.15,
      disableHd: opts.disableHd ?? false,
      deviceBitmapCache: opts.deviceBitmapCache ?? true,
      imageSmoothingQuality: opts.imageSmoothingQuality ?? 'low',
    };
  }

  private beginFrame(): void {
    this.frameNo++;
    this.outer = null;
    this.stats = emptyStats();
    this.surfaces.beginFrame();
    const blink = this.opts.caretBlinkMs;
    this.caretOn = blink <= 0 || Math.floor(this.opts.now() / blink) % 2 === 0;
  }

  private canvasSize(): { w: number; h: number } {
    const c: any = (this.ctx as any).canvas;
    return { w: c?.width ?? 0, h: c?.height ?? 0 };
  }

  /**
   * Render the whole stage. `view` maps stage coordinates to device pixels (scale + letterbox
   * offset, see BrowserHost). Areas outside the stage rectangle get `letterboxColor`; content
   * outside the stage is clipped when `letterbox` is true (default, like Ruffle).
   * PORT (wide screens): `visible` (stage coordinates) replaces the stage rectangle as the shown area: content is
   * drawn (on the stage colour) and clipped there instead, and the letterbox colour fills only what lies beyond it.
   * Objects flagged `$designClip` stay clipped to the stage rectangle.
   */
  renderStage(
    stage: DisplayObject & { stageWidth: number; stageHeight: number; color?: number },
    view: Viewport,
    options: {
      width?: number;
      height?: number;
      letterbox?: boolean;
      letterboxColor?: string;
      clear?: boolean;
      visible?: { x: number; y: number; width: number; height: number } | null;
      /** Damage rendering: draw this frame whole (the host knows the canvas or something outside the display list changed). */
      full?: boolean;
    } = {},
  ): void {
    const size = this.canvasSize();
    const W = options.width ?? size.w;
    const H = options.height ?? size.h;
    const ctx = this.ctx;
    const vm: Mat = [view.scale, 0, 0, view.scaleY ?? view.scale, view.tx, view.ty];
    let plan: DamagePlan | null = null;
    if (this.damage) {
      const v = options.visible;
      const key = [W, H, ...vm, v ? `${v.x},${v.y},${v.width},${v.height}` : '-', options.letterbox !== false, options.letterboxColor ?? '#000', options.clear !== false, stage.color ?? 0, this.sharpLargeBitmaps].join('|');
      this.damage.setView(vm);
      // (a cache tile built in the last frame is drawn from this one: its pixels differ from what it replaces)
      plan = this.damage.plan(stage, vm, W, H, key, !!options.full, this.surfaces.unstable);
      // Nothing visible changed.
      if (plan && plan.rects.length === 0) return;
    }
    this.beginFrame();
    this.rootToDevice = vm;
    this.fsx = Math.abs(vm[0]) || 1;
    this.fsy = Math.abs(vm[3]) || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    const stageBox = rectBox(vm, 0, 0, stage.stageWidth, stage.stageHeight);
    const v = options.visible;
    const shownBox = v ? rectBox(vm, v.x, v.y, v.width, v.height) : stageBox;
    this.designBox = v ? stageBox : null;
    let clip: Box = { x0: 0, y0: 0, x1: W, y1: H };
    if (plan) {
      // A partial frame: everything below is drawn as in a whole one, clipped to the damage (whole device pixels).
      ctx.save();
      ctx.beginPath();
      for (const r of plan.rects) ctx.rect(r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0);
      ctx.clip();
      clip = plan.bbox;
      this.damageRects = plan.rects.length > 1 ? plan.rects : null;
      this.damageAll = plan.rects;
    }
    // (the letterbox colour only where the shown area leaves some: a wide view fills the canvas with the stage colour)
    if (options.clear !== false && !(shownBox.x0 <= clip.x0 && shownBox.y0 <= clip.y0 && shownBox.x1 >= clip.x1 && shownBox.y1 >= clip.y1)) {
      ctx.fillStyle = options.letterboxColor ?? '#000';
      ctx.fillRect(0, 0, W, H);
    }
    ctx.save();
    if (options.letterbox !== false) {
      clip = boxIntersect(clip, shownBox);
      ctx.beginPath();
      ctx.rect(shownBox.x0, shownBox.y0, shownBox.x1 - shownBox.x0, shownBox.y1 - shownBox.y0);
      ctx.clip();
    }
    ctx.fillStyle = cssColor((stage.color ?? 0) >>> 0, 1);
    ctx.fillRect(shownBox.x0, shownBox.y0, shownBox.x1 - shownBox.x0, shownBox.y1 - shownBox.y0);
    this.drawChildren(stage, vm, IDENTITY_CT, { ctx, clip, isLayer: false }, false);
    ctx.restore();
    if (plan) ctx.restore();
    this.damageRects = null;
    this.damageAll = null;
    this.designBox = null;
    this.endFrame();
  }

  /** Culling of a leaf's device box: outside the target's clip, or (a partial frame's canvas) all its rectangles. */
  private culled(b: Box, t: Target): boolean {
    if (!boxIntersects(b, t.clip)) return true;
    const d = this.damageRects;
    if (!d || t.isLayer) return false;
    for (let i = 0; i < d.length; i++) if (boxIntersects(b, d[i])) return false;
    return true;
  }

  /** PORT (wide screens): the stage rectangle in device pixels while renderStage shows more than it (else null). */
  private designBox: Box | null = null;

  /**
   * Render one display object (with its own transform, visibility, colour transform, filters...)
   * where `base` maps its parent's coordinate space to device pixels. Used for tests, thumbnails
   * and snapshots; `clear` clears the canvas to transparent first.
   */
  renderDisplayObject(obj: DisplayObject, base: Mat, options: { width?: number; height?: number; clear?: boolean } = {}): void {
    const size = this.canvasSize();
    const W = options.width ?? size.w;
    const H = options.height ?? size.h;
    const ctx = this.ctx;
    this.damage?.invalidate();
    this.beginFrame();
    const parent = obj.parent as DisplayObject | null;
    const inv = parent ? invert(rd.globalMatrix(parent)) : null;
    this.rootToDevice = inv ? mul(base, inv) : base.slice(0, 6) as Mat;
    this.fsx = Math.hypot(base[0], base[1]) || 1;
    this.fsy = Math.hypot(base[2], base[3]) || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    if (options.clear) ctx.clearRect(0, 0, W, H);
    this.drawNode(obj, base, IDENTITY_CT, { ctx, clip: { x0: 0, y0: 0, x1: W, y1: H }, isLayer: false }, false);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    this.endFrame();
  }

  private endFrame(): void {
    this.surfaces.endFrame();
    this.trimFilterCache();
  }

  /**
   * PORT: how many shape images the shown objects under `root` need that are not decoded yet (each is requested now):
   * a host holds its last frame over a screen that would show without its art (BrowserHost `holdFrame`). Hidden
   * objects and masks do not count (what a frame draws, save culling).
   */
  missingImages(root: DisplayObject): number {
    let n = 0;
    const walk = (o: DisplayObject): void => {
      if (!rd.isVisible(o) || rd.isMask(o) || !(rd.alpha(o) > 0)) return;
      const info = rd.shapeImage(o);
      if (info && !info.image) {
        this.request(info.url);
        n++;
      }
      for (const c of rd.renderChildren(o)) walk(c);
    };
    walk(root);
    return n;
  }

  /** Forget lazily requested image URLs (after a library reload). */
  resetImageRequests(): void {
    this.requested.clear();
  }

  // ------------------------------------------------------------------ tree walk

  private drawNode(o: DisplayObject, pm: Mat, pct: CT, t: Target, maskMode: boolean): void {
    if (!maskMode && (!rd.isVisible(o) || rd.isMask(o))) return;
    // A partial frame: a subtree whose record misses the damage draws nothing there (not walked).
    const da = this.damageAll;
    if (da && !t.isLayer && !maskMode && this.damage!.outside(o, da)) {
      this.stats.culled++;
      return;
    }
    // PORT (wide screens): an object clipped to the stage rectangle (on the stage's own target; the port flags only
    // parts of screens, never content of cached layers).
    const db = this.designBox;
    if (db && !maskMode && !t.isLayer && o.$designClip) {
      const clip = boxIntersect(t.clip, db);
      if (boxEmpty(clip)) return;
      t.ctx.save();
      t.ctx.setTransform(1, 0, 0, 1, 0, 0);
      t.ctx.beginPath();
      t.ctx.rect(db.x0, db.y0, db.x1 - db.x0, db.y1 - db.y0);
      t.ctx.clip();
      try {
        this.drawNodeClipped(o, pm, pct, { ctx: t.ctx, clip, isLayer: false }, maskMode);
      } finally {
        t.ctx.restore();
      }
      return;
    }
    this.drawNodeClipped(o, pm, pct, t, maskMode);
  }

  private drawNodeClipped(o: DisplayObject, pm: Mat, pct: CT, t: Target, maskMode: boolean): void {
    this.stats.nodes++;
    const m = mul(pm, rd.localMatrix(o));
    if (maskMode) {
      this.drawContent(o, m, IDENTITY_CT, t, true);
      return;
    }
    const own = rd.colorTransform(o);
    const ct = isIdentityCT(own) ? pct : concatCT(pct, own);
    if (isInvisibleColor(ct, this.outer)) return;
    const fl = rd.filters(o);
    let specs: FilterSpec[] | null = null;
    let distributable = true;
    if (fl.length) {
      for (const f of fl) {
        const s = filterSpec(f);
        if (!s) continue;
        if (s.kind === 'colorMatrix') {
          if (isIdentityMatrix(s.matrix)) continue;
          if (!isDistributableMatrix(s.matrix)) distributable = false;
        } else distributable = false;
        (specs ??= []).push(s);
      }
    }
    const bm = rd.blendMode(o);
    const mk = rd.mask(o);
    const blended = bm !== 'normal' && (bm !== 'layer' || !isIdentityCT(ct));
    if (specs && distributable && !mk && !blended) {
      // Leaf-level colour matrices: content -> F1 -> ... -> Fn -> ct -> outer stages.
      const saved = this.outer;
      const stages: Array<CT | CM> = saved ? [...saved] : [];
      if (!isIdentityCT(ct)) stages.push(ct);
      for (let i = specs.length - 1; i >= 0; i--) stages.push((specs[i] as { matrix: number[] }).matrix);
      this.outer = stages;
      try {
        this.drawContent(o, m, IDENTITY_CT, t, false);
      } finally {
        this.outer = saved;
      }
      return;
    }
    if (specs || mk || blended) {
      this.drawLayered(o, m, ct, t, specs ?? [], bm, mk);
      return;
    }
    this.drawContent(o, m, ct, t, false);
  }

  private drawContent(o: DisplayObject, m: Mat, ct: CT, t: Target, maskMode: boolean): void {
    const sr = rd.scrollRect(o);
    let cm = m;
    let tt = t;
    if (sr) {
      const box = rectBox(m, 0, 0, sr.width, sr.height);
      const vis = boxIntersect(box, t.clip);
      if (boxEmpty(vis)) return;
      t.ctx.save();
      t.ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
      t.ctx.beginPath();
      t.ctx.rect(0, 0, sr.width, sr.height);
      t.ctx.clip();
      cm = translate(m, -sr.x, -sr.y);
      tt = { ctx: t.ctx, clip: vis, isLayer: t.isLayer };
    }
    if (!maskMode) {
      const ob = rd.opaqueBackground(o);
      if (ob !== null) this.fillOpaqueBackground(o, sr ? m : cm, ob, ct, tt, sr);
    }
    switch (rd.renderKind(o)) {
      case 'shape': {
        this.drawShapeImage(o, cm, ct, tt, maskMode);
        const g = rd.graphicsCommands(o);
        if (g && g.length) drawGraphics(tt.ctx, g, cm, ct, this.surfaces, maskMode, this.outer);
        break;
      }
      case 'morph':
        this.drawShapeImage(o, cm, ct, tt, maskMode);
        break;
      case 'bitmap':
        this.drawBitmap(o, cm, ct, tt, maskMode);
        break;
      case 'text':
        if (!maskMode) this.drawTextField(o, cm, ct, tt);
        break;
      case 'container':
      case 'stage': {
        const g = rd.graphicsCommands(o);
        if (g && g.length) drawGraphics(tt.ctx, g, cm, ct, this.surfaces, maskMode, this.outer);
        this.drawChildren(o, cm, ct, tt, maskMode);
        break;
      }
      case 'button':
        this.drawChildren(o, cm, ct, tt, maskMode);
        break;
      default:
        break;
    }
    if (sr) t.ctx.restore();
  }

  /** Children in render order, grouping timeline clip layers (clipDepth) with the siblings they mask. */
  private drawChildren(o: DisplayObject, m: Mat, ct: CT, t: Target, maskMode: boolean): void {
    const kids = rd.renderChildren(o);
    const n = kids.length;
    let i = 0;
    while (i < n) {
      const c = kids[i];
      const cd = rd.clipDepth(c);
      if (cd > 0) {
        const d0 = rd.depth(c);
        let j = i + 1;
        while (j < n) {
          const dj = rd.depth(kids[j]);
          if (dj > d0 && dj <= cd) j++;
          else break;
        }
        if (maskMode) {
          // Inside a mask, nested clip layers are ignored (members drawn unclipped).
          for (let k = i + 1; k < j; k++) this.drawNode(kids[k], m, ct, t, true);
        } else if (j > i + 1) this.drawClipGroup(c, kids, i + 1, j, m, ct, t);
        i = j;
        continue;
      }
      this.drawNode(c, m, ct, t, maskMode);
      i++;
    }
  }

  /**
   * If `maskObj` is a plain shape whose raster is a fully opaque rectangle, its device transform
   * and size (clip-path fast path); else null.
   */
  private rectMask(maskObj: DisplayObject, mm: Mat): { m: Mat; w: number; h: number } | null {
    if (rd.renderKind(maskObj) !== 'shape') return null;
    const g = rd.graphicsCommands(maskObj);
    if (g && g.length) return null;
    const info = rd.shapeImage(maskObj);
    if (!info || !info.image) return null;
    const ref = info.ref;
    const img = info.image;
    const sx = ref.sx ?? 0;
    const sy = ref.sy ?? 0;
    const sw = ref.sw ?? img.width;
    const sh = ref.sh ?? img.height;
    if (!this.surfaces.isOpaque(img, sx, sy, sw, sh)) return null;
    const k = 1 / (ref.scale || 1);
    return { m: mul(mm, [k, 0, 0, k, ref.offsetX, ref.offsetY]), w: sw, h: sh };
  }

  private drawClipGroup(maskObj: DisplayObject, kids: DisplayObject[], from: number, to: number, m: Mat, ct: CT, t: Target): void {
    this.stats.maskGroups++;
    const mm = mul(m, rd.localMatrix(maskObj));
    const rect = this.rectMask(maskObj, mm);
    if (rect) {
      const vis = boxIntersect(rectBox(rect.m, 0, 0, rect.w, rect.h), t.clip);
      if (boxEmpty(vis)) return;
      this.stats.clipMasks++;
      const ctx = t.ctx;
      ctx.save();
      ctx.setTransform(rect.m[0], rect.m[1], rect.m[2], rect.m[3], rect.m[4], rect.m[5]);
      ctx.beginPath();
      ctx.rect(0, 0, rect.w, rect.h);
      ctx.clip();
      const tt: Target = { ctx, clip: vis, isLayer: t.isLayer };
      for (let k = from; k < to; k++) this.drawNode(kids[k], m, ct, tt, false);
      ctx.restore();
      return;
    }
    const b = rd.boundsWithMatrix(maskObj, mm);
    if (!b) return;
    const box = boxRoundOut(boxIntersect(boxPad({ x0: b.xMin, y0: b.yMin, x1: b.xMax, y1: b.yMax }, 2), t.clip));
    if (boxEmpty(box)) return;
    const w = box.x1 - box.x0;
    const h = box.y1 - box.y0;
    const toLayer: Mat = [1, 0, 0, 1, -box.x0, -box.y0];
    const lm = mul(toLayer, m);
    const layer = this.surfaces.acquireLayer(w, h);
    this.stats.layers++;
    const lt: Target = { ctx: layer.ctx, clip: { x0: 0, y0: 0, x1: w, y1: h }, isLayer: true };
    for (let k = from; k < to; k++) this.drawNode(kids[k], lm, ct, lt, false);
    this.applyMaskLayer(layer, w, h, (mt) => this.drawNode(maskObj, lm, IDENTITY_CT, mt, true));
    this.composite(t, layer, box, 1, 'source-over');
    this.surfaces.releaseLayer(layer);
  }

  /** Render a mask into a fresh layer and keep `layer` only where the mask has coverage. */
  private applyMaskLayer(layer: Surface, w: number, h: number, drawMask: (target: Target) => void): void {
    const ml = this.surfaces.acquireLayer(w, h);
    this.stats.layers++;
    drawMask({ ctx: ml.ctx, clip: { x0: 0, y0: 0, x1: w, y1: h }, isLayer: true });
    const lc = layer.ctx;
    lc.setTransform(1, 0, 0, 1, 0, 0);
    lc.globalAlpha = 1;
    lc.globalCompositeOperation = 'destination-in';
    lc.drawImage(ml.canvas as any, 0, 0, w, h, 0, 0, w, h);
    lc.globalCompositeOperation = 'source-over';
    this.surfaces.releaseLayer(ml);
  }

  /** Draw `layer` (region [0,w)x[0,h)) onto the target at `box` with alpha and composite op. */
  private composite(t: Target, layer: Surface, box: Box, alpha: number, op: GlobalCompositeOperation): void {
    const ctx = t.ctx;
    const w = box.x1 - box.x0;
    const h = box.y1 - box.y0;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = alpha;
    ctx.globalCompositeOperation = op;
    ctx.imageSmoothingEnabled = false;
    if (op === 'destination-in') {
      // Bounded to the object's box (Canvas applies destination-in to the whole target otherwise).
      ctx.save();
      ctx.beginPath();
      ctx.rect(box.x0, box.y0, w, h);
      ctx.clip();
      ctx.drawImage(layer.canvas as any, 0, 0, w, h, box.x0, box.y0, w, h);
      ctx.restore();
    } else ctx.drawImage(layer.canvas as any, 0, 0, w, h, box.x0, box.y0, w, h);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /**
   * Objects with filters, a `mask` or a blend mode: render the content into a layer (without the
   * object's own colour transform), apply filters, then colour transform, mask and blend mode.
   * Filtered results are cached per object (Flash also caches filtered objects as bitmaps) and
   * reused while the subtree signature, the matrix (up to whole-pixel translation) and the filter
   * parameters are unchanged.
   */
  private drawLayered(o: DisplayObject, m: Mat, ct: CT, t: Target, specs: FilterSpec[], bm: string, mk: DisplayObject | null): void {
    let px = 0;
    let py = 0;
    for (const s of specs) {
      const p = filterPadding(s, this.fsx, this.fsy);
      px += p.x;
      py += p.y;
    }
    const b = rd.boundsWithMatrix(o, m);
    if (!b) return;
    const full = boxRoundOut(boxPad({ x0: b.xMin, y0: b.yMin, x1: b.xMax, y1: b.yMax }, px + 2, py + 2));
    let box = boxIntersect(full, boxPad(t.clip, px, py));
    let maskPM: Mat | null = null;
    if (mk) {
      const mp = mk.parent as DisplayObject | null;
      maskPM = mp ? mul(this.rootToDevice, rd.globalMatrix(mp)) : this.rootToDevice;
      const mb = rd.boundsWithMatrix(mk, mul(maskPM, rd.localMatrix(mk)));
      if (!mb) return;
      box = boxIntersect(box, boxPad({ x0: mb.xMin, y0: mb.yMin, x1: mb.xMax, y1: mb.yMax }, 2));
    }
    box = boxRoundOut(box);
    if (boxEmpty(box)) return;

    // 1. Content + filters -> `src` covering device box `srcBox` (without any colour: the layer
    //    result gets `ct` and the outer stages below, like Flash's cached bitmap).
    const outer = this.outer;
    this.outer = null;
    try {
      this.drawLayeredInner(o, m, ct, t, specs, bm, mk, full, box, maskPM, outer);
    } finally {
      this.outer = outer;
    }
  }

  private drawLayeredInner(
    o: DisplayObject,
    m: Mat,
    ct: CT,
    t: Target,
    specs: FilterSpec[],
    bm: string,
    mk: DisplayObject | null,
    full: Box,
    box: Box,
    maskPM: Mat | null,
    outer: ColorStages | null,
  ): void {
    let src: Surface;
    let srcBox: Box;
    const temps: Surface[] = [];
    const fullArea = (full.x1 - full.x0) * (full.y1 - full.y0);
    // The size limit is in stage pixels (device area / device scale²): on HiDPI / large viewports the same
    // on-screen object covers more device pixels, and exceeding a device-pixel limit disabled the cache there,
    // re-running the CPU blur/glow every frame (measured: ~800 ms per frame at 3840x2160).
    if (specs.length && this.filterCacheBudget > 0 && fullArea <= this.filterCacheMaxArea * Math.max(1, this.fsx * this.fsy)) {
      const c = this.cachedFilterLayer(o, m, full, specs);
      src = c.surface;
      srcBox = c.box;
    } else {
      const w = box.x1 - box.x0;
      const h = box.y1 - box.y0;
      src = this.surfaces.acquireLayer(w, h);
      temps.push(src);
      this.stats.layers++;
      this.renderContentLayer(o, m, box, src, specs);
      srcBox = box;
    }

    // 2. Colour transform / mask / invert need a private copy of the visible part.
    const needPixelCT = (!isIdentityCT(ct) && !isAlphaOnlyCT(ct)) || !!outer;
    let work = src;
    let wbox = srcBox;
    if ((needPixelCT || mk || bm === 'invert') && !temps.includes(src)) {
      const w = box.x1 - box.x0;
      const h = box.y1 - box.y0;
      work = this.surfaces.acquireLayer(w, h);
      temps.push(work);
      this.stats.layers++;
      work.ctx.drawImage(src.canvas as any, box.x0 - srcBox.x0, box.y0 - srcBox.y0, w, h, 0, 0, w, h);
      wbox = box;
    }
    const w = wbox.x1 - wbox.x0;
    const h = wbox.y1 - wbox.y0;
    let alpha = 1;
    if (needPixelCT) {
      const lc = work.ctx;
      lc.setTransform(1, 0, 0, 1, 0, 0);
      const id = lc.getImageData(0, 0, w, h);
      applyColorToRGBA(id.data, ct, outer, true);
      lc.putImageData(id, 0, 0);
    } else alpha = ct[3];
    if (mk && maskPM) {
      this.stats.maskGroups++;
      const mpm = mul([1, 0, 0, 1, -wbox.x0, -wbox.y0], maskPM);
      this.applyMaskLayer(work, w, h, (mt) => this.drawNode(mk, mpm, IDENTITY_CT, mt, true));
    }
    if (bm === 'invert') {
      const lc = work.ctx;
      lc.setTransform(1, 0, 0, 1, 0, 0);
      lc.globalCompositeOperation = 'source-in';
      lc.fillStyle = '#fff';
      lc.fillRect(0, 0, w, h);
      lc.globalCompositeOperation = 'source-over';
      this.composite(t, work, wbox, alpha, 'difference');
    } else if ((bm === 'erase' || bm === 'alpha') && !t.isLayer) {
      // UNVERIFIED: Flash applies erase/alpha only inside a parent with blendMode "layer"; drawn normally otherwise.
      this.composite(t, work, wbox, alpha, 'source-over');
    } else this.composite(t, work, wbox, alpha, COMPOSITE[bm] ?? 'source-over');
    for (const s of temps) this.surfaces.releaseLayer(s);
  }

  /** Render `o`'s content (identity colour transform) into `layer` covering device box `box`, then apply filters. */
  private renderContentLayer(o: DisplayObject, m: Mat, box: Box, layer: Surface, specs: FilterSpec[]): void {
    const w = box.x1 - box.x0;
    const h = box.y1 - box.y0;
    const lt: Target = { ctx: layer.ctx, clip: { x0: 0, y0: 0, x1: w, y1: h }, isLayer: true };
    this.drawContent(o, mul([1, 0, 0, 1, -box.x0, -box.y0], m), IDENTITY_CT, lt, false);
    if (!specs.length) return;
    const lc = layer.ctx;
    lc.setTransform(1, 0, 0, 1, 0, 0);
    lc.globalAlpha = 1;
    const id = lc.getImageData(0, 0, w, h);
    for (const s of specs) applyFilterSpec(id, s, this.fsx, this.fsy);
    lc.putImageData(id, 0, 0);
  }

  // ------------------------------------------------------------------ filter cache

  private readonly filterCache = new Map<DisplayObject, FilterCacheEntry>();
  private filterCachePixels = 0;
  private readonly ids = new WeakMap<object, number>();
  private nextId = 1;
  private frameNo = 0;

  private idOf(o: object): number {
    let id = this.ids.get(o);
    if (!id) this.ids.set(o, (id = this.nextId++));
    return id;
  }

  private cachedFilterLayer(o: DisplayObject, m: Mat, full: Box, specs: FilterSpec[]): { surface: Surface; box: Box } {
    const fx = Math.floor(m[4]);
    const fy = Math.floor(m[5]);
    const key = JSON.stringify(specs) + '|' + this.fsx + ',' + this.fsy;
    const sig: number[] = [];
    this.signature(o, sig, true);
    const e = this.filterCache.get(o);
    if (
      e &&
      e.key === key &&
      e.m[0] === m[0] &&
      e.m[1] === m[1] &&
      e.m[2] === m[2] &&
      e.m[3] === m[3] &&
      e.m[4] - Math.floor(e.m[4]) === m[4] - fx &&
      e.m[5] - Math.floor(e.m[5]) === m[5] - fy &&
      sameSig(e.sig, sig)
    ) {
      e.lastFrame = this.frameNo;
      this.stats.filterCacheHits++;
      return { surface: e.surface, box: { x0: e.rel.x0 + fx, y0: e.rel.y0 + fy, x1: e.rel.x1 + fx, y1: e.rel.y1 + fy } };
    }
    const w = full.x1 - full.x0;
    const h = full.y1 - full.y0;
    let surface: Surface;
    if (e && e.surface.width >= w && e.surface.height >= h && e.surface.width * e.surface.height <= 4 * w * h) {
      surface = e.surface;
      const c = surface.ctx;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.globalAlpha = 1;
      c.globalCompositeOperation = 'source-over';
      c.clearRect(0, 0, w, h);
    } else {
      if (e) this.dropFilterEntry(o, e);
      surface = this.surfaces.createSurface(w, h, true);
    }
    this.stats.layers++;
    this.renderContentLayer(o, m, full, surface, specs);
    const px = surface.width * surface.height;
    const entry: FilterCacheEntry = {
      sig,
      key,
      m: m.slice(0, 6) as Mat,
      rel: { x0: full.x0 - fx, y0: full.y0 - fy, x1: full.x1 - fx, y1: full.y1 - fy },
      surface,
      pixels: px,
      lastFrame: this.frameNo,
    };
    if (e && e.surface === surface) this.filterCachePixels -= e.pixels;
    this.filterCache.set(o, entry);
    this.filterCachePixels += px;
    return { surface, box: full };
  }

  private dropFilterEntry(o: DisplayObject, e: FilterCacheEntry): void {
    this.filterCache.delete(o);
    this.filterCachePixels -= e.pixels;
    e.surface.canvas.width = 1;
    e.surface.canvas.height = 1;
  }

  /** Evict filter-cache entries unused for a while or beyond the pixel budget (oldest first). */
  private trimFilterCache(): void {
    for (const [o, e] of this.filterCache) {
      if (this.frameNo - e.lastFrame > 120) this.dropFilterEntry(o, e);
    }
    if (this.filterCachePixels > this.filterCacheBudget) {
      const entries = [...this.filterCache.entries()].sort((a, b) => a[1].lastFrame - b[1].lastFrame);
      for (const [o, e] of entries) {
        if (this.filterCachePixels <= this.filterCacheBudget) break;
        if (e.lastFrame === this.frameNo) continue;
        this.dropFilterEntry(o, e);
      }
    }
  }

  /** Drop all cached filter results. */
  clearFilterCache(): void {
    for (const [o, e] of [...this.filterCache]) this.dropFilterEntry(o, e);
  }

  /**
   * Signature of everything that affects the rendered content of `o`'s subtree (not its own
   * transform / colour transform / filters, which are handled outside the cache): object ids and
   * render versions (every visual setter, timeline change, text/graphics edit bumps them), bitmap
   * pixel versions, lazily decoded images, text focus/caret state, masks used inside the subtree.
   */
  private signature(o: DisplayObject, out: number[], root: boolean): void {
    const kind = rd.renderKind(o);
    out.push(this.idOf(o));
    const g = rd.graphicsCommands(o);
    if (!root || kind === 'text') out.push(rd.versionOf(o));
    else {
      const sr = rd.scrollRect(o);
      if (sr) out.push(sr.x, sr.y, sr.width, sr.height);
      else out.push(-1e9);
      const ob = rd.opaqueBackground(o);
      out.push(ob === null ? -1 : ob);
      out.push(g ? this.idOf(g) : 0, g ? g.length : 0);
    }
    if (kind === 'shape' || kind === 'morph') {
      const info = rd.shapeImage(o);
      out.push(info ? this.idOf(info.ref) : 0, info && info.image ? 1 : 0);
    } else if (kind === 'bitmap') {
      const bmp = rd.bitmapOf(o);
      out.push(bmp ? this.idOf(bmp.bitmapData) : 0, bmp ? bmp.bitmapData.$version : -1, bmp && bmp.smoothing ? 1 : 0);
    } else if (kind === 'text') {
      const st: any = (o as any).stage;
      out.push(st && st.focus === o ? (this.caretOn ? 2 : 1) : 0);
    }
    if (g) for (const c of g) if (c.op === 'beginBitmapFill') out.push(this.idOf(c.bitmap), (c.bitmap as any).$version);
    if (!root) {
      const mk = rd.mask(o);
      if (mk) {
        out.push(-2, ...rd.globalMatrix(mk));
        this.signature(mk, out, false);
      }
    }
    const kids = rd.renderChildren(o);
    out.push(kids.length);
    for (const k of kids) this.signature(k, out, false);
  }

  private fillOpaqueBackground(o: DisplayObject, m: Mat, color: number, ct: CT, t: Target, sr: { width: number; height: number } | null): void {
    let x = 0;
    let y = 0;
    let w = 0;
    let h = 0;
    if (sr) {
      w = sr.width;
      h = sr.height;
    } else {
      const b = rd.boundsWithMatrix(o, IDENTITY);
      if (!b) return;
      x = b.xMin;
      y = b.yMin;
      w = b.xMax - b.xMin;
      h = b.yMax - b.yMin;
    }
    const ctx = t.ctx;
    ctx.setTransform(m[0], m[1], m[2], m[3], m[4], m[5]);
    ctx.globalAlpha = 1;
    ctx.fillStyle = cssColor(color, 1, ct, this.outer);
    ctx.fillRect(x, y, w, h);
  }

  // ------------------------------------------------------------------ leaves

  private request(url: string): void {
    if (!this.imageLoader || this.requested.has(url)) return;
    this.requested.add(url);
    try {
      const p = this.imageLoader(url);
      if (p && typeof (p as Promise<unknown>).then === 'function') {
        (p as Promise<unknown>).then(
          () => this.onImageLoaded?.(),
          () => {
            /* missing image: stays undrawn */
          },
        );
      }
    } catch {
      /* ignore */
    }
  }

  private drawShapeImage(o: DisplayObject, m: Mat, ct: CT, t: Target, maskMode: boolean): void {
    const info = rd.shapeImage(o);
    if (!info) return;
    const img = info.image;
    if (!img) {
      this.request(info.url);
      return;
    }
    const ref = info.ref;
    const sx = ref.sx ?? 0;
    const sy = ref.sy ?? 0;
    const sw = ref.sw ?? img.width;
    const sh = ref.sh ?? img.height;
    if (!(sw > 0 && sh > 0)) return;
    const k = 1 / (ref.scale || 1);
    const im: Mat = [m[0] * k, m[1] * k, m[2] * k, m[3] * k, m[0] * ref.offsetX + m[2] * ref.offsetY + m[4], m[1] * ref.offsetX + m[3] * ref.offsetY + m[5]];
    if (this.culled(rectBox(im, 0, 0, sw, sh), t)) {
      this.stats.culled++;
      return;
    }
    let src: CanvasImageSource;
    let ox = sx;
    let oy = sy;
    let alpha = 1;
    const outer = maskMode ? null : this.outer;
    // Untinted draws use the atlas (alpha-only transforms through globalAlpha), others a cached
    // colour-transformed copy of the sub-rectangle.
    const plain = maskMode || (!outer && (isIdentityCT(ct) || isAlphaOnlyCT(ct)));
    if (plain && !maskMode) alpha = ct[3];
    if (plain && !this.surfaces.subImages) src = this.surfaces.image(img);
    else {
      const tinted = this.surfaces.tintedImage(img, sx, sy, sw, sh, plain ? IDENTITY_CT : ct, plain ? null : outer);
      src = tinted.source;
      ox = tinted.x;
      oy = tinted.y;
    }
    const ctx = t.ctx;
    ctx.setTransform(im[0], im[1], im[2], im[3], im[4], im[5]);
    ctx.globalAlpha = alpha;
    const smooth = ref.src.startsWith('bitmaps/') ? this.opts.bitmapFillSmoothing : this.opts.shapeSmoothing;
    const f = smooth ? 1 : this.sharpFactor(im);
    // Offline HD variant (xBR) of a raw bitmap drawn magnified: same geometry, texels 1/scale the size.
    if (f > 1 && plain && info.hdUrl && ref.hd && !this.opts.disableHd) {
      const hd = info.hdImage;
      if (hd) {
        const k = 1 / ref.hd.scale;
        ctx.setTransform(im[0] * k, im[1] * k, im[2] * k, im[3] * k, im[4], im[5]);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'low';
        ctx.drawImage(this.surfaces.image(hd), sx * ref.hd.scale, sy * ref.hd.scale, sw * ref.hd.scale, sh * ref.hd.scale, 0, 0, sw * ref.hd.scale, sh * ref.hd.scale);
        ctx.globalAlpha = 1;
        this.stats.images++;
        this.stats.hdImages++;
        return;
      }
      this.request(info.hdUrl); // until it is decoded: sharp bilinear from the 1x texels below
    }
    if (f > 1) {
      // Visual-only resampling: the display object's geometry and the texel grid are unchanged.
      const p = this.surfaces.prescaled(src, ox, oy, sw, sh, f);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'low';
      ctx.drawImage(p.source, p.x, p.y, sw * f, sh * f, 0, 0, sw, sh);
    } else {
      ctx.imageSmoothingEnabled = smooth;
      if (smooth) ctx.imageSmoothingQuality = this.opts.imageSmoothingQuality;
      ctx.drawImage(src, ox, oy, sw, sh, 0, 0, sw, sh);
    }
    ctx.globalAlpha = 1;
    this.stats.images++;
  }

  private drawBitmap(o: DisplayObject, m: Mat, ct: CT, t: Target, maskMode: boolean): void {
    const b = rd.bitmapOf(o);
    if (!b) return;
    const bd = b.bitmapData;
    const w = bd.$width as number;
    const h = bd.$height as number;
    let bm = m;
    if (this.culled(rectBox(m, 0, 0, w, h), t)) {
      this.stats.culled++;
      return;
    }
    // Flash pixel snapping: 'always', or 'auto' when drawn unrotated at ~100% scale.
    if (b.pixelSnapping !== 'never' && Math.abs(m[1]) < 1e-9 && Math.abs(m[2]) < 1e-9) {
      if (b.pixelSnapping === 'always' || (Math.abs(m[0] - 1) < 0.001 && Math.abs(m[3] - 1) < 0.001)) {
        bm = [m[0], 0, 0, m[3], Math.round(m[4]), Math.round(m[5])];
      }
    }
    let src: CanvasImageSource;
    let alpha = 1;
    const outer = maskMode ? null : this.outer;
    if (maskMode || (!outer && isIdentityCT(ct))) src = this.surfaces.bitmap(bd);
    else if (!outer && isAlphaOnlyCT(ct)) {
      src = this.surfaces.bitmap(bd);
      alpha = ct[3];
    } else src = this.surfaces.tintedBitmap(bd, ct, outer);
    const ctx = t.ctx;
    ctx.setTransform(bm[0], bm[1], bm[2], bm[3], bm[4], bm[5]);
    ctx.globalAlpha = alpha;
    let f = b.smoothing ? 1 : this.sharpFactor(bm);
    const large = w * h > LARGE_BITMAP_PX;
    if (f > 1 && large && !this.sharpLargeBitmaps) f = 1;
    if (f > 1 && src !== this.surfaces.bitmap(bd)) f = 1; // colour-transformed bitmaps keep plain nearest sampling (rare)
    const axisAligned = bm[1] === 0 && bm[2] === 0 && bm[0] > 0 && bm[3] > 0;
    if (f > 1 && axisAligned && this.opts.deviceBitmapCache && this.surfaces.deviceBitmapCacheable(bd, w, h, bm[0], bm[3])) {
      this.surfaces.drawDeviceBitmap(ctx, bd, bm, f); // terrain, water, HUD bitmaps: cached at device resolution, blitted 1:1
    } else {
      if (large) this.largeBitmapDraws++;
      if (f > 1) f = this.surfaces.bitmapPrescaleFactor(w, h, f);
      if (f > 1) {
        this.surfaces.drawSharpBitmap(ctx, bd, f);
      } else {
        ctx.imageSmoothingEnabled = !!b.smoothing;
        if (b.smoothing) ctx.imageSmoothingQuality = this.opts.imageSmoothingQuality;
        ctx.drawImage(src, 0, 0, w, h, 0, 0, w, h);
      }
    }
    ctx.globalAlpha = 1;
    this.stats.bitmaps++;
  }

  /**
   * Quality ladder step used by hosts under load: sharp bilinear for large BitmapData (the terrain; its bilinear
   * sampling costs ~3 ms per 1080p frame on software-rasterized canvases) can be switched off before the
   * resolution is lowered.
   */
  sharpLargeBitmaps = true;
  /**
   * Cumulative count of large BitmapData draws whose cost depends on `sharpLargeBitmaps` (resampled sharp bilinear,
   * or nearest while it is off); lets hosts attribute render time to the terrain. Draws served by the
   * device-resolution cache are not counted: that 1:1 blit is cheaper than nearest, so there is nothing to shed.
   */
  largeBitmapDraws = 0;

  /** Integer prescale factor for sharp bilinear at transform `m` (device px per texel), 1 = draw as is. */
  private sharpFactor(m: Mat): number {
    if (!this.opts.sharpBilinear) return 1;
    const mag = Math.max(Math.hypot(m[0], m[1]), Math.hypot(m[2], m[3]));
    if (!(mag >= this.opts.sharpBilinearMin)) return 1;
    // Exact integer magnification: nearest is already crisp and even.
    if (Math.abs(mag - Math.round(mag)) < 0.02) return 1;
    return Math.min(4, Math.ceil(mag));
  }

  private drawTextField(o: DisplayObject, m: Mat, ct: CT, t: Target): void {
    if (!this.opts.drawText) return;
    const info = rd.textInfo(o);
    if (!info) return;
    const bb = info.bounds;
    if (this.culled(rectBox(m, bb.x, bb.y, bb.width, bb.height), t)) {
      this.stats.culled++;
      return;
    }
    let defaultColor = 0;
    if (info.focused && info.type === 'input') {
      const f = (o as any).defaultTextFormat;
      if (f && f.color !== null && f.color !== undefined) defaultColor = Number(f.color) >>> 0;
    }
    drawText(t.ctx, info, m, ct, { fonts: this.fonts, caretVisible: this.caretOn, defaultColor, outer: this.outer });
    this.stats.texts++;
  }
}

export { defaultCanvasFactory };
export type { CanvasFactory };
